import PaymentGateway from "@constants/PaymentGateway";
import type Currency from "@interfaces/Currency";

/**
 * Which rail takes cards issued in Russia (МИР and locally issued
 * Visa/Mastercard) on the eSIM plans checkout: `cloud-payments` (default) or
 * `arcopay`. Both integrations stay in the code; only one is offered at a time,
 * so switching back is a build setting, not a code change.
 */
export const russianCardGateway =
  process.env.GATSBY_RUSSIAN_CARD_GATEWAY === PaymentGateway.ARCOPAY
    ? PaymentGateway.ARCOPAY
    : PaymentGateway.CLOUD_PAYMENTS;

/**
 * CloudPayments exists only for a build that selects it and was given a
 * publishable key (the CloudPayments Public ID), so an unconfigured environment
 * keeps the plain Stripe-only checkout. Must match `stripe_key.publishable_key`
 * of an active `gateway='cloud-payments'` row in PTS.
 */
export const isCloudPaymentsEnabled =
  russianCardGateway === PaymentGateway.CLOUD_PAYMENTS &&
  ((process.env.GATSBY_CLOUDPAYMENTS_ID ?? "") !== "" ||
    (process.env.GATSBY_TEST_CLOUDPAYMENTS_ID ?? "") !== "");

/**
 * ArcoPay is the same, for `gateway='arcopay'` rows. Paused for now (its live
 * terminal was never authorised); select it with
 * GATSBY_RUSSIAN_CARD_GATEWAY="arcopay" when it comes back.
 */
export const isArcoPayEnabled =
  russianCardGateway === PaymentGateway.ARCOPAY &&
  ((process.env.GATSBY_ARCOPAY_ID ?? "") !== "" ||
    (process.env.GATSBY_TEST_ARCOPAY_ID ?? "") !== "");

/**
 * Whether the build offers a Russian-card gateway at all. layout.tsx starts
 * Russian visitors on RUB only when it does.
 */
export const isRussianCardEnabled = isCloudPaymentsEnabled || isArcoPayEnabled;

/**
 * The only currency the Russian-card rail settles in: its terminals charge in
 * roubles, so switching to that gateway has to switch the currency with it.
 */
export const russianCardCurrency = "RUB";

/**
 * Whether the visitor's IP (`countryCodeFromApi`, see layout.tsx) is in
 * Russia: they start on RUB and get the switch to the Russian-card gateway.
 */
export const isRussianVisitor = (countryCodeFromApi: string): boolean =>
  countryCodeFromApi.toUpperCase() === "RU";

/**
 * Set when the visitor picks a currency in the currency selector, so the RUB
 * default for Russian visitors (layout.tsx) stops overriding their choice for
 * the rest of the browser session.
 */
const CURRENCY_CHOSEN_BY_VISITOR_KEY = "bnesim_currency_chosen_by_visitor";

export const markCurrencyChosenByVisitor = (): void => {
  try {
    sessionStorage.setItem(CURRENCY_CHOSEN_BY_VISITOR_KEY, "1");
  } catch {
    // Storage blocked: the RUB default just keeps applying on page load.
  }
};

export const isCurrencyChosenByVisitor = (): boolean => {
  try {
    return sessionStorage.getItem(CURRENCY_CHOSEN_BY_VISITOR_KEY) !== null;
  } catch {
    return false;
  }
};

export const defaultGlobalGateway = PaymentGateway.STRIPE;

export const allowedGateways: PaymentGateway[] = [
  PaymentGateway.STRIPE,
  ...(isCloudPaymentsEnabled ? [PaymentGateway.CLOUD_PAYMENTS] : []),
  ...(isArcoPayEnabled ? [PaymentGateway.ARCOPAY] : []),
];

/**
 * Gateways offered on the eSIM plans checkout.
 *
 * The Russian-card gateway is settled before `processOrder` runs (the
 * CloudPayments widget charges client-side; ArcoPay by its callback), so the
 * browser comes back to an already-PAID transaction. Only `purchaseESIM` handles that -- it returns the
 * settled transaction as-is -- which is why it is offered here and nowhere
 * else.
 */
export const esimPlansGateways: PaymentGateway[] = allowedGateways;

/**
 * Gateways for every other checkout (BNE Guard VPN, BNE GO, SIM cards).
 *
 * Stripe only: `purchaseSIM` and `purchaseBNEGuard` look the transaction up
 * excluding PAID and otherwise re-run the Stripe completion, so a Russian-card
 * order there would report a failure for money the shopper has already paid.
 * Those checkouts stay on Stripe until PTS grows the same handling.
 */
export const defaultCheckoutGateways: PaymentGateway[] = [
  PaymentGateway.STRIPE,
];

/**
 * The currency a Stripe-only checkout moves to when it finds RUB selected.
 * RUB is settled by the Russian-card gateway alone, and layout.tsx starts every
 * visitor from Russia on it, so without this those checkouts would mint Stripe
 * intents in a currency Stripe cannot charge. Prefers the site default, then
 * any currency advertising Stripe, then any other than RUB.
 */
export const stripeCheckoutCurrencyInsteadOfRub = (
  currencies: Currency[],
): string | undefined => {
  const candidates = currencies.filter(
    item => item.currency !== russianCardCurrency,
  );
  const siteDefault = process.env.GATSBY_DEFAULT_CURRENCY ?? "EUR";
  return (
    candidates.find(
      item =>
        item.currency === siteDefault &&
        item.gateways?.includes(PaymentGateway.STRIPE) !== false,
    ) ??
    candidates.find(
      item => item.gateways?.includes(PaymentGateway.STRIPE) === true,
    ) ??
    candidates[0]
  )?.currency;
};

export const vatPercentages: Record<string, number> = {
  [PaymentGateway.STRIPE]: 0,
  [PaymentGateway.ARCOPAY]: 0,
  // The CloudPayments terminal adds 22% VAT on top of the (net) order amount, as for
  // the Kaspersky shop: the checkout shows that VAT and the gross total.
  [PaymentGateway.CLOUD_PAYMENTS]: 0.22,
};

/**
 * Net amount plus VAT, rounded the way CloudPayments rounds the amount it charges:
 * to the cent, an exact half cent going to the even cent (903.25 + 22% = 1101.965
 * -> 1101.96; 267.75 + 22% = 326.655 -> 326.66). Computed in whole cents so
 * floating point cannot shift it, so the total shown is exactly what is charged.
 * A zero rate returns the net amount unchanged.
 */
export const addVat = (net: number, rate: number): number => {
  const ratePercent = Math.round(rate * 100);
  if (ratePercent === 0) {
    return Math.round(net * 100) / 100;
  }
  // Net in cents times (100 + rate%): the gross amount in hundredths of a cent.
  const scaled = Math.round(net * 100) * (100 + ratePercent);
  const cents = Math.floor(scaled / 100);
  const remainder = scaled - cents * 100;
  const rounded =
    remainder > 50 || (remainder === 50 && cents % 2 === 1) ? cents + 1 : cents;
  return rounded / 100;
};

// The `app` this site sends to /esim_app_status/ (none: the BNESIM eSIM app):
// its `status` shows/hides the wallet and "pay with crypto" payment options,
// as it does in the app.
export const appStatusApp: string = "";
export const walletCashbackPercentages = 5;
